import { ChangeDetectionStrategy, Component } from '@angular/core';
import { BlsUpdate, BlsUpdatesLocalStoreService, BlsUpdateState, UpdateType } from './bls-updates-local-store.service';
import { getEnumValues } from '../../../core/utilities';
import { combineLatestWith, Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { map } from 'rxjs/operators';
import { blsUpdateRowAnimation } from '../animations/row-animation';
import { getFormattedDifferences, prepareForDiffChecking } from '../diff-utils';
import { RowActions } from '../location-list/state/location-list-actions';
import { LocationListLocalStoreService } from '../location-list/location-list-local-store.service';
import { selectFocusedLocationId } from '../location-list/state/location-list-selectors';
import { LocationListApiService } from '../location-list/location-list-api.service';
import { JumperComponent } from '../../global/jumper/jumper.component';
import { MatProgressBar } from '@angular/material/progress-bar';
import { LetDirective } from '@ngrx/component';
import { ColDef, GridOptions, GridReadyEvent, GridSizeChangedEvent, RowDataUpdatedEvent, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { DEFAULT_GRID_COLUMN_DEFS, DEFAULT_GRID_OPTIONS } from '../constants';
import { AgMultiSelectFilterComponent } from '../../global/gx-ag-grid/ag-multi-select-filter/ag-multi-select-filter.component';
import { AgListFloatingFilterComponent } from '../../global/gx-ag-grid/ag-list-floating-filter/ag-list-floating-filter.component';
import { AgSingleSelectFilterComponent } from '../../global/gx-ag-grid/ag-single-select-filter/ag-single-select-filter.component';
import { formatLocalizedDate } from '../../../core/utils/date-fns-utils';
import { AgGridAngular } from 'ag-grid-angular';
import { DiffCellComponent } from './diff-cell/diff-cell.component';
import { AgBasicHeaderComponent } from '../../global/gx-ag-grid/ag-basic-header/ag-basic-header.component';
import { AgDateTimeFilterComponent } from '../../global/gx-ag-grid/ag-date-time-filter/ag-date-time-filter.component';
import { AgDateTimeFloatingFilterComponent } from '../../global/gx-ag-grid/ag-date-time-floating-filter/ag-date-time-floating-filter.component';

@Component({
    selector: 'app-bls-updates',
    templateUrl: './bls-updates.component.html',
    styleUrls: ['./bls-updates.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [BlsUpdatesLocalStoreService],
    animations: [blsUpdateRowAnimation],
    standalone: true,
  imports: [
    LetDirective,
    MatProgressBar,
    JumperComponent,
    AgGridAngular,
  ],
})
export class BlsUpdatesComponent {
  readonly state$: Observable<BlsUpdateState> = this.localStore.blsUpdatesState$;
  readonly viewModel$: Observable<{ state: BlsUpdateState, total: number, focusedRowOneBasedIndex: number }> =
      this.localStore.blsUpdatesState$
          .pipe(combineLatestWith(this.locationListLocalStore.total$, this.store.select(selectFocusedLocationId)), map(
              ([blsUpdatesState, total, focusedLoacationId]) => {
                return {
                  state: blsUpdatesState,
                  total,
                  focusedRowOneBasedIndex: this.getCurrentRowOneBasedIndex(focusedLoacationId)
                };
              }),
          );

  readonly defaultColumnDefs: ColDef<BlsUpdate> = {
    ...DEFAULT_GRID_COLUMN_DEFS,
    resizable: false,
    autoHeight: true,
    wrapText: true,
    suppressMovable: true,
  };

  readonly gridOptions: GridOptions = {
    ...DEFAULT_GRID_OPTIONS,
    suppressCellFocus: true,
    // getRowId:
    // Since the ID for this is the locationID, it is not unique, but as there are no data
    // transactions, it's ok to just use the default row index as the ID
  };

  readonly columnDefs: ColDef<BlsUpdate>[] = [
    {
      headerName: 'Field Name',
      headerComponent: AgBasicHeaderComponent,
      field: 'fieldName',
      valueFormatter: (params: ValueFormatterParams<BlsUpdate>) => this.localStore.getFieldNameByFieldID(params.value),
      filter: AgMultiSelectFilterComponent,
      filterParams: {
        filterOptions: this.localStore.sortedFieldNameFilterOptions$
      },
      floatingFilter: true,
      floatingFilterComponent: AgListFloatingFilterComponent,
      cellDataType: 'text',
      minWidth: 156,
      maxWidth: 256
    },
    {
      headerName: 'Previous Value',
      headerComponent: AgBasicHeaderComponent,
      field: 'previousValue',
      valueFormatter: (params: ValueFormatterParams<BlsUpdate>) => this.formatDifference(params.data),
      cellDataType: 'text',
      minWidth: 256,
      cellRenderer: DiffCellComponent
    },
    {
      headerName: 'Applied Value',
      headerComponent: AgBasicHeaderComponent,
      field: 'currentValue',
      valueFormatter: (params: ValueFormatterParams<BlsUpdate>) => this.formatDifference(params.data),
      cellDataType: 'text',
      minWidth: 256,
      cellRenderer: DiffCellComponent
    },
    {
      headerName: 'Time',
      headerComponent: AgBasicHeaderComponent,
      headerComponentParams: { enableMenu: true },
      field: 'timestamp',
      cellDataType: 'number',
      valueGetter: (params: ValueGetterParams<BlsUpdate>) => {
        return params.data?.timestamp ? new Date(params.data.timestamp) : null;
      },
      valueFormatter: (params: ValueFormatterParams<BlsUpdate>) => {
        return params.value ? formatLocalizedDate(params.value, 'dd.MM.yyyy, HH:mm') : '';
      },
      filter: AgDateTimeFilterComponent,
      floatingFilter: true,
      floatingFilterComponent: AgDateTimeFloatingFilterComponent,
      minWidth: 176,
      maxWidth: 176,
    },
    {
      headerName: 'Update Type',
      headerComponent: AgBasicHeaderComponent,
      field: 'type',
      cellDataType: 'text',
      filter: AgSingleSelectFilterComponent,
      filterParams: {
        filterOptions: getEnumValues(UpdateType)
            .map((value: UpdateType) => {
              return {name: value, value: value};
            }),
      },
      floatingFilter: true,
      floatingFilterComponent: AgListFloatingFilterComponent,
      minWidth: 156,
      maxWidth: 156
    },
  ]

  constructor(private localStore: BlsUpdatesLocalStoreService,
              private store: Store,
              private locationListLocalStore: LocationListLocalStoreService,
              private locationListApi: LocationListApiService) {
  }

  /**
   * Reuse the diff formatter from `ShowBlsGmbDifferencesPopupComponent` as per BLS-3721.
   * @param updateEntry
   */
  private formatDifference(updateEntry: BlsUpdate) {
    const fieldId = updateEntry.fieldName;

    return getFormattedDifferences({
      // we're pretending as if our prev/curr values correspond to gmb/bls values to
      // comply with the `ShowBlsGmbDifferencesPopupComponent` conventions
      gmbValue: prepareForDiffChecking(fieldId, updateEntry['previousValue']),
      blsValue: prepareForDiffChecking(fieldId, updateEntry['currentValue']),
      fieldId,
    });
  }

  private getCurrentRowOneBasedIndex(focusedLocationId: number): number | undefined {
    if (!focusedLocationId) {
      return undefined;
    }

    return this.locationListApi.getRowById(focusedLocationId.toString())?.rowIndex + 1;
  }

  onGridReady(e: GridReadyEvent<BlsUpdate>) {
    e.api.sizeColumnsToFit();
  }

  onGridSizeChanged(e: GridSizeChangedEvent<BlsUpdate>) {
    e.api.sizeColumnsToFit();
  }

  onRowDataUpdated(e: RowDataUpdatedEvent<BlsUpdate>) {
    e.api.sizeColumnsToFit();
  }

  /**
   * Change the data source for this component and scroll data row into view
   * @param nextRowOneBasedIndex
   */
  changeCurrentRow(nextRowOneBasedIndex: number) {
    const nextFocusedRow = this.locationListApi.getRowByIndex(nextRowOneBasedIndex - 1);
    this.store.dispatch(RowActions.updateFocusedRow({ id: Number(nextFocusedRow.id) }));
    this.locationListApi.scrollRowToView(nextFocusedRow);
  };

}
